import React, { useEffect, useState } from "react";
import Card from "../../components/Card/Card";
import { toast } from "react-toastify";
import http from "../../http-common";
import {
  Button,
  Checkbox,
  DataTable,
  Loading,
  RadioButton,
  RadioButtonGroup,
} from "carbon-components-react";
import { TableContainer } from "carbon-components-react";
import { Table } from "carbon-components-react";
import { TableHead } from "carbon-components-react";
import { TableRow } from "carbon-components-react";
import { TableBody } from "carbon-components-react";
import { TableCell } from "carbon-components-react";
import { TableHeader } from "carbon-components-react";

const tableHeaders = [
  { header: "Seleccionar", key: "selected" },
  { header: "Nome", key: "firstName" },
  { header: "Apelido", key: "lastName" },
  { header: "Telefone", key: "phoneNumber" },
  { header: "Desempate", key: "tiebreaker" },
  { header: "Planos de Negócio Atribuídos", key: "totalAssignedBusinessPlans" },
  { header: "Avaliados", key: "totalEvaluated" },
  { header: "Por Avaliar", key: "totalRemaining" },
];

const BusinessPlanJuryPanelPage = () => {
  const [windowCode, setWindowCode] = useState("1");
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [juryAssigned, setJuryAssigned] = useState(false);

  const [juries, setJuries] = useState([]);
  const [selectedJuryIds, setSelectedJuryIds] = useState([]);
  const [selectedTiebreakJuryIds, setSelectedTiebreakJuryIds] = useState([]);

  const nonTiebreakerJuries = juries.filter((jury) => !jury.tiebreaker);
  const tiebreakerJuries = juries.filter((jury) => jury.tiebreaker);
  const selectedCount =
    selectedJuryIds.length + selectedTiebreakJuryIds.length;
  const allJuriesSelected =
    juries.length > 0 && selectedCount === juries.length;
  const someJuriesSelected = selectedCount > 0 && !allJuriesSelected;

  const fetchJuries = async () => {
    try {
      const response = await http.get("/business-plan/juries");
      setJuries(response.data);
      // Everyone available is pre-selected by default; the admin unchecks
      // whoever should be left out of this particular draw.
      setSelectedJuryIds(
        response.data.filter((j) => !j.tiebreaker).map((j) => j.id)
      );
      setSelectedTiebreakJuryIds(
        response.data.filter((j) => j.tiebreaker).map((j) => j.id)
      );
    } catch (error) {
      toast.error("Erro ao carregar a lista de júris");
    }
  };

  const countEvaluations = async () => {
    try {
      const response = await http.get(
        `/business-plan/count-evaluations?windowCode=${windowCode}`
      );
      setJuryAssigned(response.data > 0);
    } catch (error) {
      toast.error("Erro ao carregar dados estatísticos");
    }
  };

  const getStats = async () => {
    try {
      const response = await http.get(
        `/business-plan/jury-panel-stats?windowCode=${windowCode}`
      );
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar dados estatísticos");
    }
  };

  useEffect(() => {
    fetchJuries();
  }, []);

  useEffect(() => {
    countEvaluations();
    getStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowCode]);

  const toggleSelected = (setter, id) => {
    setter((prev) =>
      prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id]
    );
  };

  const toggleJury = (jury) => {
    toggleSelected(
      jury.tiebreaker
        ? setSelectedTiebreakJuryIds
        : setSelectedJuryIds,
      jury.id
    );
  };

  const toggleAllJuries = () => {
    if (allJuriesSelected) {
      setSelectedJuryIds([]);
      setSelectedTiebreakJuryIds([]);
      return;
    }

    setSelectedJuryIds(nonTiebreakerJuries.map((jury) => jury.id));
    setSelectedTiebreakJuryIds(tiebreakerJuries.map((jury) => jury.id));
  };

  const isJurySelected = (jury) =>
    jury.tiebreaker
      ? selectedTiebreakJuryIds.includes(jury.id)
      : selectedJuryIds.includes(jury.id);

  const assignBusinessPlansToJury = async () => {
    if (selectedJuryIds.length < 2) {
      toast.error("Seleccione pelo menos 2 júris para efectuar a atribuição.");
      return;
    }
    try {
      setLoading(true);
      await http.post("/business-plan/assign-jury", {
        windowCode: Number(windowCode),
        juryIds: selectedJuryIds,
      });
      toast.success("Atribuição de Planos de Negócio executada com sucesso");
      setJuryAssigned(true);
      getStats();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data);
    }
    setLoading(false);
  };

  const assignBusinessPlansToTiebreakerJury = async () => {
    if (selectedTiebreakJuryIds.length === 0) {
      toast.error("Seleccione pelo menos um júri de desempate.");
      return;
    }
    try {
      setLoading(true);
      const response = await http.post("/business-plan/assign-tiebreak-jury", {
        windowCode: Number(windowCode),
        juryIds: selectedTiebreakJuryIds,
      });
      toast.success(response.data);
      getStats();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.response?.data);
    }
    setLoading(false);
  };

  return (
    <Card title="Painel de júri - Planos de Negócio">
      <Loading active={loading} description="A processar..." withOverlay={true} />

      <RadioButtonGroup
        legendText="Janela"
        name="jury-panel-window"
        valueSelected={windowCode}
        onChange={(value) => setWindowCode(value)}
      >
        <RadioButton labelText="Janela 1" value="1" id="jury-panel-window-1" />
        <RadioButton labelText="Janela 2" value="2" id="jury-panel-window-2" />
      </RadioButtonGroup>

      <div
        className="mt-2 mb-2"
        style={{ display: "flex", justifyContent: "flex-end", gap: "1rem" }}
      >
        {!juryAssigned && (
          <Button onClick={assignBusinessPlansToJury}>
            Sortear Planos de Negócio pelos Júris Seleccionados
          </Button>
        )}
        <Button onClick={assignBusinessPlansToTiebreakerJury}>
          Atribuir Júri de Desempate
        </Button>
      </div>

      <DataTable
        rows={stats}
        headers={tableHeaders}
        isSortable
        useZebraStyles
      >
        {({ rows, headers, getHeaderProps, getTableProps }) => (
          <TableContainer title="Estatísticas por Júri">
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map((header) =>
                    header.key === "selected" ? (
                      <TableHeader key={header.key}>
                        <Checkbox
                          id="select-all-juries"
                          labelText="Todos"
                          checked={allJuriesSelected}
                          indeterminate={someJuriesSelected}
                          onChange={toggleAllJuries}
                        />
                      </TableHeader>
                    ) : (
                      <TableHeader
                        key={header.key}
                        {...getHeaderProps({ header })}
                      >
                        {header.header}
                      </TableHeader>
                    )
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => {
                  const jury = juries.find(
                    (item) => String(item.username) === String(row.id)
                  );

                  return (
                    <TableRow key={row.id}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>
                          {cell.info.header === "selected" && jury ? (
                            <Checkbox
                              id={`select-jury-${jury.id}`}
                              labelText=""
                              hideLabel
                              checked={isJurySelected(jury)}
                              onChange={() => toggleJury(jury)}
                            />
                          ) : (
                            cell.value
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataTable>
    </Card>
  );
};

export default BusinessPlanJuryPanelPage;
